"use client";

// ============================================================
// /login — email + password sign-in, plus first-login setup.
// ============================================================
// Replaces the old "pick your name from a dropdown" flow. Two modes on one
// card:
//   • signin  → POST /api/auth/login  { email, password }
//   • setup   → POST /api/auth/setup  { email, password, code? }
// The server sets the httpOnly session cookie; on success we bounce to the
// app (?next= if present). Errors are deliberately generic — the server
// never tells the client which of email / password / active was wrong.
import { useEffect, useState } from "react";
import { C, PRODUCT } from "../brand";


const MONO = "var(--font-display)";
const SANS = "var(--font-body)";

function safeNext() {
  if (typeof window === "undefined") return "/";
  const sp = new URLSearchParams(window.location.search);
  const next = sp.get("next");
  // Only allow same-origin relative paths (defense against open-redirect).
  if (next && next.startsWith("/") && !next.startsWith("//")) return next;
  return "/";
}

export default function LoginPage() {
  const [mode, setMode] = useState("signin"); // "signin" | "setup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [code, setCode] = useState("");
  const [codeRequired, setCodeRequired] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Learn whether the first-login flow needs a shared team code. Server-only
  // config; the code itself never reaches the client.
  useEffect(() => {
    let cancelled = false;
    fetch("/api/auth/config", { credentials: "include" })
      .then(r => (r.ok ? r.json() : null))
      .then(cfg => { if (!cancelled && cfg) setCodeRequired(!!cfg.setupCodeRequired); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  function switchMode(next) {
    setMode(next);
    setError("");
    setPassword("");
    setConfirm("");
    setCode("");
  }

  async function submit(e) {
    if (e) e.preventDefault();
    if (busy) return;
    setError("");

    const em = email.trim();
    if (!em || !password) {
      setError(mode === "setup" ? "Unable to set password" : "Invalid credentials");
      return;
    }

    if (mode === "setup") {
      if (password.length < 8) {
        setError("Password must be at least 8 characters");
        return;
      }
      if (password !== confirm) {
        setError("Passwords do not match");
        return;
      }
      if (codeRequired && !code.trim()) {
        setError("Team code is required");
        return;
      }
    }

    setBusy(true);
    try {
      const url = mode === "setup" ? "/api/auth/setup" : "/api/auth/login";
      const body =
        mode === "setup"
          ? { email: em, password, code: code.trim() || undefined }
          : { email: em, password };
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (res.ok) {
        window.location.href = safeNext();
        return;
      }
      let msg = mode === "setup" ? "Unable to set password" : "Invalid credentials";
      try {
        const data = await res.json();
        if (data && data.error) msg = data.error;
      } catch {}
      setError(msg);
    } catch {
      setError("Network error — please try again");
    } finally {
      setBusy(false);
    }
  }

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "11px 13px",
    fontSize: 15,
    fontFamily: SANS,
    color: C.navy,
    background: C.white,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    outline: "none",
  };
  const labelStyle = {
    display: "block",
    fontSize: 11,
    fontFamily: MONO,
    fontWeight: 600,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    color: C.muted,
    marginBottom: 6,
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: C.bg,
        padding: 20,
        fontFamily: SANS,
      }}
    >
      <form
        onSubmit={submit}
        style={{
          width: "100%",
          maxWidth: 380,
          background: C.white,
          border: `1px solid ${C.border}`,
          borderRadius: 16,
          boxShadow: "0 8px 40px rgba(13,46,81,0.10)",
          padding: "34px 30px 30px",
        }}
      >
        {/* Brand */}
        <div style={{ textAlign: "center", marginBottom: 26 }}>
          <div
            style={{
              fontFamily: MONO,
              fontSize: 20,
              fontWeight: 700,
              letterSpacing: 1,
              color: C.navy,
            }}
          >
            {PRODUCT.name.toUpperCase()}
          </div>
          <div
            style={{
              fontFamily: MONO,
              fontSize: 11,
              letterSpacing: 1,
              color: C.muted,
              marginTop: 6,
              textTransform: "uppercase",
            }}
          >
            {mode === "setup" ? "Set your password" : "Sign in"}
          </div>
        </div>

        {/* Email */}
        <div style={{ marginBottom: 16 }}>
          <label htmlFor="login-email" style={labelStyle}>Email</label>
          <input
            id="login-email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={e => setEmail(e.target.value)}
            style={inputStyle}
            disabled={busy}
            autoFocus
          />
        </div>

        {/* Password */}
        <div style={{ marginBottom: 16 }}>
          <label htmlFor="login-password" style={labelStyle}>
            {mode === "setup" ? "New password" : "Password"}
          </label>
          <input
            id="login-password"
            type="password"
            autoComplete={mode === "setup" ? "new-password" : "current-password"}
            value={password}
            onChange={e => setPassword(e.target.value)}
            style={inputStyle}
            disabled={busy}
          />
        </div>

        {/* Setup-only fields */}
        {mode === "setup" && (
          <>
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="login-confirm" style={labelStyle}>Confirm password</label>
              <input
                id="login-confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                style={inputStyle}
                disabled={busy}
              />
            </div>
            {codeRequired && (
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="login-code" style={labelStyle}>Team code</label>
                <input
                  id="login-code"
                  type="text"
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  style={inputStyle}
                  disabled={busy}
                />
              </div>
            )}
          </>
        )}

        {/* Error */}
        {error && (
          <div
            role="alert"
            style={{
              background: C.errorBg,
              border: `1px solid #fecdca`,
              color: C.error,
              fontSize: 13,
              borderRadius: 8,
              padding: "9px 12px",
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={busy}
          style={{
            width: "100%",
            padding: "12px 0",
            fontSize: 14,
            fontFamily: MONO,
            fontWeight: 700,
            letterSpacing: 0.8,
            textTransform: "uppercase",
            color: C.white,
            background: busy ? C.muted : C.navy,
            border: "none",
            borderRadius: 8,
            cursor: busy ? "default" : "pointer",
          }}
        >
          {busy ? "Please wait…" : mode === "setup" ? "Set password & sign in" : "Sign in"}
        </button>

        {/* Mode toggle */}
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <button
            type="button"
            onClick={() => switchMode(mode === "setup" ? "signin" : "setup")}
            disabled={busy}
            style={{
              background: "none",
              border: "none",
              color: C.blue,
              fontSize: 13,
              fontFamily: SANS,
              cursor: "pointer",
              padding: 4,
            }}
          >
            {mode === "setup"
              ? "Already have a password? Sign in"
              : "First time? Set your password"}
          </button>
        </div>
      </form>
    </div>
  );
}
